import React from 'react';

// Shown beneath a "distress" persona reply. Static, app-side copy — not
// generated by the model — so a real resource is guaranteed to appear
// regardless of how the reply's prose turned out. Framed as "support
// resources", never "crisis"/"emergency" (App Store copy rules; the web app
// and the shipped binaries must not diverge on this wording).
//
// Mirrors mobile/lib/features/chat/presentation/widgets/support_resources_card.dart.
// The two tables are duplicated on purpose — the card must render with zero
// network dependency on both platforms — so any edit here needs the same edit
// there.
//
// Verified 2026-08-23 against primary/official sources. All are 24/7 and free.
//   US  988lifeline.org                    (SAMHSA / FCC)
//   GB  samaritans.org                     (116 123, free, not itemised)
//   IN  telemanas.mohfw.gov.in             (Govt. of India, 20+ languages)
//   NP  Patan Mental Hospital / WHO / TPO  (1166)
//
// KEEP IN SYNC with the published store countries in RELEASES/README.md — live
// countries as of 2026-08-19 are IN, NP, US, GB.
const LINES = {
    US: 'In the United States, call or text 988 — the Suicide & Crisis Lifeline.',
    GB: 'In the United Kingdom, call 116 123 — Samaritans, free and not shown on your phone bill.',
    IN: 'In India, call 14416 — Tele-MANAS, available in English and 20 regional languages.',
    NP: 'In Nepal, call 1166 — the National Suicide Prevention Helpline.',
};

const INTERNATIONAL = 'Anywhere else, findahelpline.com lists free listeners by country.';

// IANA zone → country, for the published countries only. Timezone is checked
// before language because it tracks where the person physically is; a browser
// set to en-US in Delhi is common, a machine on Asia/Kolkata that is not in
// India is not. Both signals are advisory — the international line renders
// regardless, so a wrong guess still leaves a working route.
const ZONE_TO_REGION = {
    'Asia/Kolkata': 'IN',
    'Asia/Calcutta': 'IN',
    'Asia/Kathmandu': 'NP',
    'Asia/Katmandu': 'NP',
    'Europe/London': 'GB',
};

// Every browser API here is wrapped: Intl and navigator both vary across the
// browsers this app supports (iOS Safari, PWA, Android Chrome, desktop), and a
// throw on this path would take the whole distress reply down with it.
function resolveRegion() {
    try {
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (zone && ZONE_TO_REGION[zone]) return ZONE_TO_REGION[zone];
    } catch {
        // fall through to language
    }
    try {
        const tags = navigator.languages?.length
            ? navigator.languages
            : [navigator.language].filter(Boolean);
        for (const tag of tags) {
            // en-US → US; en-Latn-US → US. Region subtags are two letters.
            const region = String(tag).split('-').find((p) => /^[A-Za-z]{2}$/.test(p) && p === p.toUpperCase());
            if (region && LINES[region]) return region;
        }
    } catch {
        // fall through to international-only
    }
    return null;
}

export function SupportResources() {
    const region = resolveRegion();
    const local = region ? LINES[region] : null;

    return (
        <div
            className="mt-3 max-w-[85%] sm:max-w-xl rounded-2xl border border-emerald-200 bg-emerald-50/60 px-4 py-3"
            role="note"
            aria-label="Support resources"
        >
            <p className="font-sans text-[13px] font-semibold text-stone-700">Support resources</p>
            {local && (
                <p className="mt-1 font-sans text-[13px] leading-relaxed text-stone-600">{local}</p>
            )}
            <p className="mt-1 font-sans text-[13px] leading-relaxed text-stone-600">{INTERNATIONAL}</p>
        </div>
    );
}
